import React, {
    useState
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from 'shared/components/ui/select';
import {
    makeApiStepTemplate,
    makeChooseStepTemplate,
    makeLLMStepTemplate,
    makeMemoryStepTemplate,
    makeRagBasicSearchTemplate,
    makeRagRetrieveFilesTemplate,
    makeRagSearchFilesTemplate,
    makeSubflowStepTemplate
} from './templates';
import {
    OpcodeStepTemplate
} from './types';
import {
    List,
    Layers,
    GitBranch,
    Bot,
    Clock,
    Globe,
    Workflow,
    GitBranch as ChooseIcon,
    Folder,
    Search,
    FileSearch
} from 'lucide-react';

type InsertTarget = 'steps' | 'definitions' | 'flows';

type TemplatesPanelProps = {
    flowIndex: string;
    flowOptionsLength: number;
    setFlowIndex: (value: string) => void;
    addToSteps: (step: OpcodeStepTemplate) => void;
    addToDefinitions: (step: OpcodeStepTemplate) => void;
    addStepToSelectedFlow: (step: OpcodeStepTemplate) => void;
    addNewFlow: () => void;
};

const TemplatesPanel = ({
    flowIndex,
    flowOptionsLength,
    setFlowIndex,
    addToSteps,
    addToDefinitions,
    addStepToSelectedFlow,
    addNewFlow
}: TemplatesPanelProps) => {
    const [insertTarget, setInsertTarget] = useState<InsertTarget>('steps');

    const handleTemplateClick = (templateFn: () => OpcodeStepTemplate) => {
        const template = templateFn();
        switch (insertTarget) {
            case 'steps':
                addToSteps(template);
                break;
            case 'definitions':
                addToDefinitions(template);
                break;
            case 'flows':
                addStepToSelectedFlow(template);
                break;
        }
    };

    const flowOptions = Array.from({
        length: flowOptionsLength
    }, (_, i) => ({
        value: String(i),
        label: `Flow ${i}`
    }));

    return (
        <div className="space-y-4 pt-4">
            {/* Header */}
            <div>
                <h2 className="text-lg font-semibold">Builder Controls</h2>
            </div>

            {/* Insert Target Section */}
            <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Insert Target</h3>
                <div className="flex gap-1 p-1 bg-muted rounded-md">
                    <Button
                        variant={insertTarget === 'steps' ? 'default' : 'ghost'}
                        size="sm"
                        className={`flex-1 gap-2 ${insertTarget === 'steps' ? '' : 'hover:bg-transparent'}`}
                        onClick={() => setInsertTarget('steps')}
                    >
                        <List className="h-4 w-4" />
                        <span>STEPS</span>
                    </Button>
                    <Button
                        variant={insertTarget === 'definitions' ? 'default' : 'ghost'}
                        size="sm"
                        className={`flex-1 gap-2 ${insertTarget === 'definitions' ? '' : 'hover:bg-transparent'}`}
                        onClick={() => setInsertTarget('definitions')}
                    >
                        <Layers className="h-4 w-4" />
                        <span>DEFINITIONS</span>
                    </Button>
                    <Button
                        variant={insertTarget === 'flows' ? 'default' : 'ghost'}
                        size="sm"
                        className={`flex-1 gap-2 ${insertTarget === 'flows' ? '' : 'hover:bg-transparent'}`}
                        onClick={() => setInsertTarget('flows')}
                    >
                        <GitBranch className="h-4 w-4" />
                        <span>FLOWS</span>
                    </Button>
                </div>
            </div>

            {/* Flow Selector Section - Only shown when FLOWS is selected */}
            {insertTarget === 'flows' && (
                <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground">Flow Selector</h3>
                    <Select
                        value={flowIndex}
                        onValueChange={setFlowIndex}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select a flow" />
                        </SelectTrigger>
                        <SelectContent>
                            {flowOptions.length > 0 ? (
                                flowOptions.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))
                            ) : (
                                <SelectItem value="none" disabled>
                                    No flows available
                                </SelectItem>
                            )}
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={addNewFlow}
                        className="w-full gap-2"
                        variant="default"
                    >
                        <span className="text-lg">+</span>
                        <span>Add new flow</span>
                    </Button>
                </div>
            )}

            {/* Add Template Section */}
            <div className="space-y-3">
                <h3 className="text-sm font-medium text-muted-foreground">Add Template</h3>
                <div className="grid grid-cols-3 gap-2">
                    {/* LLM */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeLLMStepTemplate)}
                    >
                        <Bot className="h-5 w-5" />
                        <span className="text-xs">LLM</span>
                    </Button>

                    {/* Memory */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeMemoryStepTemplate)}
                    >
                        <Clock className="h-5 w-5" />
                        <span className="text-xs">Memory</span>
                    </Button>

                    {/* HTTP API */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeApiStepTemplate)}
                    >
                        <Globe className="h-5 w-5" />
                        <span className="text-xs">HTTP API</span>
                    </Button>

                    {/* Subflow */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeSubflowStepTemplate)}
                    >
                        <Workflow className="h-5 w-5" />
                        <span className="text-xs">Subflow</span>
                    </Button>

                    {/* Choose */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeChooseStepTemplate)}
                    >
                        <ChooseIcon className="h-5 w-5" />
                        <span className="text-xs">Choose</span>
                    </Button>

                    {/* Retrieve */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeRagRetrieveFilesTemplate)}
                    >
                        <Folder className="h-5 w-5" />
                        <span className="text-xs">Retrieve</span>
                    </Button>

                    {/* RAG File */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeRagSearchFilesTemplate)}
                    >
                        <FileSearch className="h-5 w-5" />
                        <span className="text-xs">RAG File</span>
                    </Button>

                    {/* RAG */}
                    <Button
                        variant="outline"
                        className="flex flex-col items-center justify-center gap-2 h-auto py-3"
                        onClick={() => handleTemplateClick(makeRagBasicSearchTemplate)}
                    >
                        <Search className="h-5 w-5" />
                        <span className="text-xs">RAG</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TemplatesPanel;
